import { useState, useEffect, Component } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Trash2, GripVertical, Image as ImageIcon, ArrowUpDown } from "lucide-react";
import {
  ReactCompareSlider,
  ReactCompareSliderImage
} from "react-compare-slider";

// Error boundary for ReactCompareSlider
class ComparisonErrorBoundary extends Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error('ReactCompareSlider Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BeforeAfterProject {
  id: string;
  before_image_url: string;
  after_image_url: string;
  title: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
}

export function ProposalBeforeAfterManager() {
  const [projects, setProjects] = useState<BeforeAfterProject[]>([]);
  const [beforeImage, setBeforeImage] = useState<File | null>(null);
  const [afterImage, setAfterImage] = useState<File | null>(null);
  const [beforePreview, setBeforePreview] = useState<string>("");
  const [afterPreview, setAfterPreview] = useState<string>("");
  const [beforeImageLoaded, setBeforeImageLoaded] = useState(false);
  const [afterImageLoaded, setAfterImageLoaded] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    const { data, error } = await supabase
      .from('proposal_before_after')
      .select('*')
      .order('display_order');

    if (error) {
      toast({
        title: "Error loading projects",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setProjects(data || []);
  };

  const handleBeforeImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBeforeImage(file);
      setBeforePreview(URL.createObjectURL(file));
      setBeforeImageLoaded(false);
    }
  };

  const handleAfterImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAfterImage(file);
      setAfterPreview(URL.createObjectURL(file));
      setAfterImageLoaded(false);
    }
  };

  const uploadImage = async (file: File, path: string) => {
    const { error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(path, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('product-images')
      .getPublicUrl(path);

    return publicUrl;
  };

  const handleCreate = async () => {
    if (!beforeImage || !afterImage || !title.trim()) {
      toast({
        title: "Missing required fields",
        description: "Please upload both before and after images, and provide a title",
        variant: "destructive",
      });
      return;
    }

    const activeProjects = projects.filter(p => p.is_active);
    if (activeProjects.length >= 2) {
      toast({
        title: "Maximum projects reached",
        description: "You can only have 2 active before/after projects. Deactivate one first.",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      const beforeExt = beforeImage.name.split('.').pop();
      const afterExt = afterImage.name.split('.').pop();
      const beforePath = `proposal-before-after/${Math.random()}.${beforeExt}`;
      const afterPath = `proposal-before-after/${Math.random()}.${afterExt}`;

      const beforeUrl = await uploadImage(beforeImage, beforePath);
      const afterUrl = await uploadImage(afterImage, afterPath);

      const maxOrder = Math.max(0, ...projects.map(p => p.display_order));

      const { error: insertError } = await supabase
        .from('proposal_before_after')
        .insert({
          before_image_url: beforeUrl,
          after_image_url: afterUrl,
          title,
          description: description || null,
          display_order: maxOrder + 1,
          is_active: true,
        });

      if (insertError) throw insertError;

      toast({
        title: "Project created",
        description: "Before/after project added successfully",
      });

      // Reset form
      setBeforeImage(null);
      setAfterImage(null);
      setBeforePreview("");
      setAfterPreview("");
      setBeforeImageLoaded(false);
      setAfterImageLoaded(false);
      setTitle("");
      setDescription("");
      loadProjects();
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (id: string, updates: Partial<BeforeAfterProject>) => {
    const { error } = await supabase
      .from('proposal_before_after')
      .update(updates)
      .eq('id', id);

    if (error) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setProjects(projects.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    const { error } = await supabase
      .from('proposal_before_after')
      .delete()
      .eq('id', deleteId);

    if (error) {
      toast({
        title: "Delete failed",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    setProjects(projects.filter(p => p.id !== deleteId));
    setDeleteId(null);
    toast({
      title: "Project deleted",
      description: "Before/after project removed",
    });
  };

  const handleReorder = async (id: string, direction: 'up' | 'down') => {
    const index = projects.findIndex(p => p.id === id);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === projects.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newProjects = [...projects];
    [newProjects[index], newProjects[newIndex]] = [newProjects[newIndex], newProjects[index]];

    await Promise.all([
      supabase.from('proposal_before_after').update({ display_order: newIndex }).eq('id', newProjects[newIndex].id),
      supabase.from('proposal_before_after').update({ display_order: index }).eq('id', newProjects[index].id),
    ]);

    setProjects(newProjects);
  };

  const activeCount = projects.filter(p => p.is_active).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Upload New Before/After Project</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Before Image</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleBeforeImageChange}
                disabled={uploading}
              />
              {beforePreview && (
                <div className="mt-2 rounded overflow-hidden border">
                  <img 
                    src={beforePreview} 
                    alt="Before preview" 
                    className="w-full h-48 object-cover"
                    onLoad={() => setBeforeImageLoaded(true)}
                    onError={() => setBeforeImageLoaded(false)}
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>After Image</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={handleAfterImageChange}
                disabled={uploading}
              />
              {afterPreview && (
                <div className="mt-2 rounded overflow-hidden border">
                  <img 
                    src={afterPreview} 
                    alt="After preview" 
                    className="w-full h-48 object-cover"
                    onLoad={() => setAfterImageLoaded(true)}
                    onError={() => setAfterImageLoaded(false)}
                  />
                </div>
              )}
            </div>
          </div>

          {beforePreview && afterPreview && (
            <div className="space-y-2">
              <Label>Preview Comparison</Label>
              {beforeImageLoaded && afterImageLoaded ? (
                <div className="aspect-video max-w-2xl mx-auto border rounded overflow-hidden">
                  <ComparisonErrorBoundary
                    fallback={
                      <div className="grid grid-cols-2 gap-2 p-4 h-full">
                        <div className="flex flex-col items-center justify-center">
                          <img src={beforePreview} alt="Before" className="w-full h-auto" />
                          <p className="text-sm text-muted-foreground mt-2">Before</p>
                        </div>
                        <div className="flex flex-col items-center justify-center">
                          <img src={afterPreview} alt="After" className="w-full h-auto" />
                          <p className="text-sm text-muted-foreground mt-2">After</p>
                        </div>
                      </div>
                    }
                  >
                    <ReactCompareSlider
                      itemOne={
                        <ReactCompareSliderImage
                          src={beforePreview}
                          alt="Before preview"
                        />
                      }
                      itemTwo={
                        <ReactCompareSliderImage
                          src={afterPreview}
                          alt="After preview"
                        />
                      }
                      position={50}
                      style={{ width: '100%', height: '100%' }}
                      className="cursor-ew-resize"
                    />
                  </ComparisonErrorBoundary>
                </div>
              ) : (
                <div className="aspect-video max-w-2xl mx-auto border rounded overflow-hidden bg-muted flex items-center justify-center">
                  <p className="text-sm text-muted-foreground">Loading images...</p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="title">Project Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Residential Roof Replacement - Main St"
              disabled={uploading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Complete tear-off and installation of GAF Timberline HDZ shingles"
              rows={3}
              disabled={uploading}
            />
          </div>

          <Button onClick={handleCreate} disabled={uploading || !beforeImage || !afterImage || !title.trim()}>
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? "Creating..." : "Create Project"}
          </Button>

          <p className="text-sm text-muted-foreground">
            Active projects: {activeCount}/2 (Only 2 projects will be shown in proposals)
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {projects.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No before/after projects yet</p>
            </CardContent>
          </Card>
        ) : (
          projects.map((project, index) => (
            <Card key={project.id}>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleReorder(project.id, 'up')}
                      disabled={index === 0}
                    >
                      <GripVertical className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleReorder(project.id, 'down')}
                      disabled={index === projects.length - 1}
                    >
                      <GripVertical className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <div className="w-32 h-24 rounded overflow-hidden bg-muted">
                      <img
                        src={project.before_image_url}
                        alt="Before"
                        className="w-full h-full object-cover"
                      />
                      <p className="text-xs text-center mt-1">Before</p>
                    </div>
                    <div className="flex items-center">
                      <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="w-32 h-24 rounded overflow-hidden bg-muted">
                      <img
                        src={project.after_image_url}
                        alt="After"
                        className="w-full h-full object-cover"
                      />
                      <p className="text-xs text-center mt-1">After</p>
                    </div>
                  </div>

                  <div className="flex-1 space-y-3">
                    <div>
                      <Label htmlFor={`title-${project.id}`}>Title</Label>
                      <Input
                        id={`title-${project.id}`}
                        value={project.title}
                        onChange={(e) => handleUpdate(project.id, { title: e.target.value })}
                        onBlur={() => loadProjects()}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`description-${project.id}`}>Description</Label>
                      <Textarea
                        id={`description-${project.id}`}
                        value={project.description || ""}
                        onChange={(e) => handleUpdate(project.id, { description: e.target.value })}
                        onBlur={() => loadProjects()}
                        rows={2}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 items-end">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`active-${project.id}`}>Active</Label>
                      <Switch
                        id={`active-${project.id}`}
                        checked={project.is_active}
                        onCheckedChange={(checked) => handleUpdate(project.id, { is_active: checked })}
                      />
                    </div>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => setDeleteId(project.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this before/after project. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
